import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { useState } from "react";

import { fetchPolicy, Policy } from "../../api/policy";
import { BackButton } from "../../components/buttons/backButton/BackButton";
import { Divider } from "../../components/dividers/Divider";
import { SearchLoader } from "../../components/loaders/searchLoader/SearchLoader";
import { useDidMount } from "../../hooks/useDidMount";
import { useQueryParams } from "../../hooks/useQueryParams";
import { force } from "../../typeguards/force";
import { AddressMutation } from "../mutations/address/AddressMutation";
import { BankAccountMutation } from "../mutations/bankAccount/BankAccountMutation";
import { EmailAddressMutation } from "../mutations/emailAddress/EmailAddressMutation";
import { OtherMutation } from "../mutations/other/OtherMutation";
import { PersonalDataMutation } from "../mutations/personalData/PersonalDataMutation";
import { PhoneNumberMutation } from "../mutations/phoneNumber/PhoneNumberMutation";
import styles from "./PolicyMutation.module.scss";

interface Props {
  fields: Record<string, Field<string> & { url: string }>;
}

export type PolicyMutationType = "address" | "bankAccount" | "phoneNumber" | "emailAddress" | "personalData" | "other";

export const QUERY_PARAM_MUTATION_TYPE = "mutationType";
export const QUERY_PARAM_POLICY_NR_ = "policyNumber";

export const PolicyMutation = ({ fields }: Props) => {
  const queryParams = useQueryParams();
  const mutationType = queryParams.get(QUERY_PARAM_MUTATION_TYPE) as PolicyMutationType | null;
  const policyNr = queryParams.get(QUERY_PARAM_POLICY_NR_);
  const [policy, setPolicy] = useState<Policy | null>(null);

  useDidMount(async () => {
    if (policyNr) {
      try {
        const fetchedPolicy = await fetchPolicy(policyNr);
        setPolicy(fetchedPolicy);
      } catch {
        console.error("Failed to fetch policy");
      }
    }
  });

  const getContent = (policyNumber: string) => {
    switch (mutationType) {
      case "address":
        return <AddressMutation fields={fields} policyNr={policyNumber} policy={force(policy)} />;
      case "bankAccount":
        return <BankAccountMutation fields={fields} policyNr={policyNumber} policy={force(policy)} />;
      case "phoneNumber":
        return <PhoneNumberMutation fields={fields} policyNr={policyNumber} />;
      case "emailAddress":
        return <EmailAddressMutation fields={fields} policyNr={policyNumber} />;
      case "personalData":
        return <PersonalDataMutation fields={fields} policyNr={policyNumber} policy={force(policy)} />;
      case "other":
        return <OtherMutation fields={fields} policyNr={policyNumber} />;
      default:
        return <SearchLoader text="Er is een onverwachte fout opgetreden" />;
    }
  };

  if (!policyNr || !mutationType || !policy) {
    return (
      <>
        <div className={styles.policyMutation__header}>
          <BackButton id="button_cancel_header">Wijziging annuleren</BackButton>
        </div>

        <Divider type="horizontal" />

        {!policy ? (
          <SearchLoader text="Polis laden..." />
        ) : (
          <SearchLoader text="Er is een onverwachte fout opgetreden" />
        )}
      </>
    );
  }

  return getContent(policyNr);
};
